import React, { useEffect, useState, useRef, useCallback } from "react";
import { getNotifications } from "../../../api/get/getNotifications";
import { t } from "i18next";

import NewItem from "../items/NewItem";
import Loading from "../../../common/components/Loading";
import app_icon_mini from '../../images/app_icon_mini.svg';

const PAGE_SIZE = 10;

const Articles = ({ service }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    const pageRef = useRef(1);
    const loadingMoreRef = useRef(false);

    const serviceType = service || "articles";

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current) return;
        const nextPage = pageRef.current + 1;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
            const data = await getNotifications({ service: serviceType }, nextPage, PAGE_SIZE);
            const items = data.items || [];
            setNotifications((prev) => [...prev, ...items]);
            setHasMore(items.length >= PAGE_SIZE);
            pageRef.current = nextPage;
        } catch (error) {
            console.error("Error fetching notifications:", error);
            setHasMore(false);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [serviceType]);

    useEffect(() => {
        setLoading(true);
        setHasMore(true);
        setNotifications([]);
        pageRef.current = 1;
        loadingMoreRef.current = false;

        let cancelled = false;
        async function fetchFirst() {
            try {
                const data = await getNotifications({ service: serviceType }, 1, PAGE_SIZE);
                if (cancelled) return;
                const items = data.items || [];
                setNotifications(items);
                setHasMore(items.length >= PAGE_SIZE);
                pageRef.current = 1;
            } catch (error) {
                if (!cancelled) console.error("Error fetching notifications:", error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchFirst();
        return () => { cancelled = true; };
    }, [serviceType]);

    useEffect(() => {
        if (!hasMore || loading || loadingMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry?.isIntersecting || loadingMoreRef.current) return;
                loadMore();
            },
            { rootMargin: '200px', threshold: 0.1 }
        );

        const el = sentinelRef.current;
        if (el) observer.observe(el);
        return () => {
            if (el) observer.unobserve(el);
        };
    }, [hasMore, loading, loadingMore, loadMore]);

    return (
        <>
            {loading ? (
                <Loading />
            ) : notifications.length > 0 ? (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 w-full py-4 md:py-0">
                        {notifications.map((notification) => (
                            <NewItem
                                key={notification.id}
                                id={notification.id}
                                name={notification.title}
                                text={notification.text}
                                date={notification.created_at}
                                file={notification.file}
                                service={service}
                            />
                        ))}
                    </div>
                    <div ref={sentinelRef} className="h-4 w-full" aria-hidden />
                    {loadingMore && (
                        <div className="flex justify-center py-6">
                            <div className="w-8 h-8 border-2 border-[#147F26] border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}
                </>
            ) : (
                <div className="flex flex-col items-center justify-center h-[371px] bg-white py-8">
                    <img
                        className="w-[75px] h-[41px] mb-5 object-contain"
                        src={app_icon_mini}
                        alt="logo"
                    />
                    <h2 className="text-base font-bold text-[var(--text-color)] m-0 mb-1">
                        {t('noNewsTitle')}
                    </h2>
                    <p className="text-base font-medium text-[var(--accent-text-color)] m-0 max-w-[358px] text-center">
                        {t('noNewsDescription')}
                    </p>
                </div>
            )}
        </>
    );
};

export default Articles;
